import React, { useEffect, useState, useMemo, useCallback } from "react";
import StandAloneVideoPlayer from "./video/StandAloneVideoPlayer";
import { getSessionPlaylist } from "redux/actions";
import { useDispatch } from "react-redux";
import OBCSpinner from "components/util/OBC";
import StandAloneImagePlayer from "./image/StandAloneImagePlayer";
import _ from "lodash";
import { Button, Slider, Menu, Dropdown, Icon, Tooltip } from "antd";
import { getSourceList } from "components/OlePatrols/olePatrolsUtils";
import { MEMOIZED_DOMAIN_URL } from "components/util/HostUtils";

const BOUNDS_FOR_EXPLORING_VIDEO = 5;

export const createPlaylistUrls = (currentSession, sourceIndex) => {
    const deviceKey = _.get(currentSession, ["device_uuid"]);
    const sessionKey = _.get(currentSession, ["uuid"]);
    const lastVidUpload = _.get(currentSession, ["last_vid_up"]);

    const rawURL = `https://raw${MEMOIZED_DOMAIN_URL}/${deviceKey}/${sessionKey}/index.m3u8?ts=${lastVidUpload}`;
    const enhancedURL = `https://raw${MEMOIZED_DOMAIN_URL}/${deviceKey}/${sessionKey}/index.m3u8?ts=${lastVidUpload}`;
    const baseURL = `https://raw${MEMOIZED_DOMAIN_URL}/${deviceKey}/${sessionKey}/`;
    const lowResURL = `https://lr${MEMOIZED_DOMAIN_URL}/${deviceKey}/${sessionKey}/index.${sourceIndex}.m3u8?ts=${lastVidUpload}`;

    return {
        raw: rawURL,
        enhanced: enhancedURL,
        base: baseURL,
        lowRes: lowResURL,
    };
};

const ButtonGroup = Button.Group;
const CONTROL_BUTTONS_SIZE = "small";

const StandAloneVideoOrImageComponent = ({ selectedSession, positionFilter = {}, startPosition = null }) => {
    const dispatch = useDispatch();

    const [currentVideoSourceIndex, setCurrentVideoSourceIndex] = useState(0);
    const [playlist, setPlaylist] = useState(null);
    const [playlistUrls, setPlaylistUrls] = useState(null);
    const [index, setIndex] = useState(0);
    const [subIndex, setSubIndex] = useState(1);
    const [isStillsMode, setStillsMode] = useState(true);

    const [selectedKey, setSelectedKey] = useState(null);
    const [selectedVideoTooltipVisible, setSelectedVideoTooltipVisible] = useState(false);

    const video = useMemo(() => {
        return _.get(playlist, ["video_playlists", currentVideoSourceIndex], []);
    }, [currentVideoSourceIndex, playlist]);

    // on session change return back to stills mode
    useEffect(() => {
        setStillsMode(true);
    }, [selectedSession]);

    const sources = useMemo(() => {
        const _playlist = {
            image: _.get(playlist, ["image_playlist"], []),
            video: _.get(playlist, ["video_playlists"], []),
        };

        const _streamInfo = _.get(selectedSession, ["stream_info"], null);

        if (!playlist || !_streamInfo) return [];

        const sourceList = getSourceList(_playlist, _streamInfo, selectedSession, true);

        return sourceList;
    }, [playlist, selectedSession]);

    const framesPerSecond = useMemo(() => {
        return _.get(video, [0, 4], 30);
    }, [video]);

    const currentPlayerPosition = useMemo(() => {
        const result = index + (subIndex - 1) / framesPerSecond;
        return result;
    }, [index, subIndex, framesPerSecond]);

    useEffect(() => {
        if (selectedSession && selectedSession.id) {
            // reset index and subIndex when session changes
            setIndex(0);
            setSubIndex(1);

            const sourceIndex = 0;
            const urls = createPlaylistUrls(selectedSession, sourceIndex);
            setPlaylistUrls(urls);

            let _positionFilter = {
                elr: positionFilter.elr,
                start_pos: parseInt(startPosition.position) - BOUNDS_FOR_EXPLORING_VIDEO,
                end_pos: parseInt(startPosition.position) + BOUNDS_FOR_EXPLORING_VIDEO,
            };
            dispatch(getSessionPlaylist(selectedSession.id, _positionFilter))
                .then((playlist) => {
                    setPlaylist(playlist);
                })
                .catch((error) => {
                    setPlaylist(null);
                    setPlaylistUrls(null);
                    console.log("Error loading session playlist: ", error);
                });
        } else {
            setPlaylist(null);
            setPlaylistUrls(null);
        }
    }, [dispatch, positionFilter.elr, selectedSession, startPosition.position]);

    const handleVideoPositionChange = useCallback(
        (newPlayerPosition) => {
            const a1 = parseInt(newPlayerPosition);
            let a2 = (newPlayerPosition - a1) * framesPerSecond;
            a2 = parseInt(a2) + 1;
            setIndex(a1);
            setSubIndex(a2);
        },
        [framesPerSecond],
    );

    const updateCurrentPlayerPosition = useCallback(
        (newPosition) => {
            const a1 = parseInt(newPosition);
            let a2 = (newPosition - a1) * framesPerSecond;
            a2 = parseInt(a2) + 1;
            setIndex(a1);
            setSubIndex(a2);
        },
        [framesPerSecond],
    );

    const videoSelectorButton = useMemo(() => {
        if (sources.length === 1) {
            return (
                <Tooltip
                    title={`${_.get(sources, [0, "name"], "No Source")}`}
                    arrowPointAtCenter={true}
                    placement="topLeft">
                    <Button
                        type={!isStillsMode ? "primary" : "default"}
                        icon="video-camera"
                        size={CONTROL_BUTTONS_SIZE}
                        onClick={() => {
                            setStillsMode(false);
                        }}
                    />
                </Tooltip>
            );
        }

        const menu = (
            <Menu
                selectedKeys={[selectedKey]}
                onClick={(info) => {
                    setSelectedKey(info.key);
                }}>
                {_.map(sources, (source, sourceIndex) =>
                    _.map(source.streams, (stream) => {
                        const key = `${sourceIndex}-${stream.name}`;

                        return (
                            <Menu.Item
                                key={key}
                                onClick={() => {
                                    if (isStillsMode) setStillsMode(false);
                                    setCurrentVideoSourceIndex(sourceIndex);
                                }}>
                                {source.name} - {stream.name}
                            </Menu.Item>
                        );
                    }),
                )}
            </Menu>
        );
        return (
            <Tooltip
                title={`Video`}
                arrowPointAtCenter={true}
                visible={selectedVideoTooltipVisible}
                onVisibleChange={(visible) => setSelectedVideoTooltipVisible(visible)}
                placement="topLeft">
                <Dropdown
                    overlay={menu}
                    trigger={["click"]}
                    onVisibleChange={() => {
                        if (selectedVideoTooltipVisible) setSelectedVideoTooltipVisible(false);
                    }}>
                    <Button
                        size={CONTROL_BUTTONS_SIZE}
                        type={isStillsMode ? "default" : "primary"}
                        icon="video-camera">
                        <Icon
                            type="down"
                            style={{ paddingLeft: "3px" }}
                        />
                    </Button>
                </Dropdown>
            </Tooltip>
        );
    }, [selectedKey, sources, isStillsMode, selectedVideoTooltipVisible]);

    const videoOrImageControlsComponent = useMemo(() => {
        return (
            <div className="OlePatrolsExploringVideoOrImagePlayerControls">
                <div className="OlePatrolsExploringPlayerSlider">
                    {isStillsMode && (
                        <Slider
                            min={0}
                            max={video.length}
                            onChange={(value) => {
                                updateCurrentPlayerPosition(value);
                            }}
                            value={currentPlayerPosition}
                            tipFormatter={null}
                            included={false}
                            step={0.01}
                        />
                    )}
                </div>

                <ButtonGroup>
                    <Tooltip
                        title="Still Images"
                        arrowPointAtCenter={true}
                        placement="topLeft">
                        <Button
                            type={isStillsMode ? "primary" : "default"}
                            icon="camera"
                            size={CONTROL_BUTTONS_SIZE}
                            onClick={() => {
                                setStillsMode(true);
                            }}
                        />
                    </Tooltip>
                    {videoSelectorButton}
                </ButtonGroup>
            </div>
        );
    }, [isStillsMode, video.length, currentPlayerPosition, videoSelectorButton, updateCurrentPlayerPosition]);

    const videoOrImagePlayer = useMemo(() => {
        return (
            <>
                {isStillsMode ? (
                    <StandAloneImagePlayer
                        playlist={playlist}
                        playlistUrls={playlistUrls}
                        setIndex={setIndex}
                        index={index}
                        subIndex={subIndex}
                        setSubIndex={setSubIndex}
                    />
                ) : (
                    <StandAloneVideoPlayer
                        selectedSession={selectedSession}
                        playlist={playlist}
                        currentPlayerPosition={currentPlayerPosition}
                        setVideoTimestamp={handleVideoPositionChange}
                    />
                )}
                {videoOrImageControlsComponent}
            </>
        );
    }, [
        isStillsMode,
        selectedSession,
        playlist,
        currentPlayerPosition,
        handleVideoPositionChange,
        playlistUrls,
        index,
        subIndex,
        videoOrImageControlsComponent,
    ]);

    if (!selectedSession) {
        return (
            <div className="StandAloneVideoOrImageComponent">
                <div>Select a session from the list below</div>
            </div>
        );
    }

    return (
        <div className={`StandAloneVideoOrImageComponent ${playlist ? "Active" : ""}`}>
            {playlist ? (
                videoOrImagePlayer
            ) : (
                <div className="StandAloneVideoOrImageComponentLoading">
                    <div>
                        <OBCSpinner
                            size={70}
                            speed={3}
                            color={"#e8dfff"}
                        />
                    </div>
                    <div className="StandAloneVideoOrImageComponentLoadingText">Loading...</div>
                </div>
            )}
        </div>
    );
};

export default StandAloneVideoOrImageComponent;
